import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { UserPlus, KeyRound, Trash2, ShieldCheck, User as UserIcon, Loader2, Pencil, Briefcase, Handshake } from "lucide-react";
import { toast } from "sonner";

const ROLE_META = {
  admin: { label: "Admin", cls: "bg-primary/12 text-primary hover:bg-primary/12", icon: ShieldCheck },
  staff: { label: "Staff", cls: "bg-[#4A6E82]/12 text-[#4A6E82] hover:bg-[#4A6E82]/12", icon: UserIcon },
  sales: { label: "Sales", cls: "bg-[#A7773F]/12 text-[#A7773F] hover:bg-[#A7773F]/12", icon: Briefcase },
  affiliate: { label: "Affiliate", cls: "bg-[#326F4B]/12 text-[#326F4B] hover:bg-[#326F4B]/12", icon: Handshake },
};

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.staff;
  const Icon = m.icon;
  return (
    <Badge variant="secondary" className={m.cls}>
      <Icon className="mr-1 h-3 w-3" />
      {m.label}
    </Badge>
  );
}

function RoleSelect({ value, onChange, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring"
    >
      <option value="staff">Staff (import & view, no user mgmt)</option>
      <option value="admin">Admin (full access)</option>
      <option value="sales">Sales (only assigned referral codes)</option>
      <option value="affiliate">Affiliate (only their own referral code)</option>
    </select>
  );
}

export default function Users() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";

  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", password: "", role: "staff", referral_codes: "" });
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "staff", referral_codes: "" });
  const [busy, setBusy] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ["users"] });

  const parseCodes = (s) => [...new Set((s || "").split(",").map((c) => c.trim()).filter(Boolean))];

  const createUser = async () => {
    setBusy(true);
    try {
      const payload = {
        name: addForm.name,
        email: addForm.email,
        password: addForm.password,
        role: addForm.role,
        referral_codes: ["sales", "affiliate"].includes(addForm.role) ? parseCodes(addForm.referral_codes) : [],
      };
      await api.post("/users", payload);
      toast.success("User created");
      setAddOpen(false);
      setAddForm({ name: "", email: "", password: "", role: "staff", referral_codes: "" });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`${u.email} is now ${role}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        referral_codes: ["sales", "affiliate"].includes(editForm.role) ? parseCodes(editForm.referral_codes) : [],
      };
      await api.patch(`/users/${editTarget.id}`, payload);
      toast.success("User updated");
      setEditTarget(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resetPw = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${pwTarget.id}/reset-password`, { password: newPw });
      toast.success("Password updated");
      setPwTarget(null);
      setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removeUser = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${delTarget.id}`);
      toast.success("User deleted");
      setDelTarget(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Users & Roles</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isAdmin ? "Manage who can access the dashboard and what they can do." : "Team members with access to this dashboard."}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setAddOpen(true)} data-testid="add-user-button">
            <UserPlus className="mr-2 h-4 w-4" /> Add user
          </Button>
        )}
      </div>

      {/* Mobile: cards */}
      <div className="space-y-3 md:hidden" data-testid="users-cards">
        {isLoading ? (
          [...Array(3)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-md border border-border bg-[#E6EFF2]" />)
        ) : (
          users.map((u, i) => (
            <div key={u.id} className="rounded-md border border-border bg-card p-4" data-testid={`user-card-${i}`}>
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-medium">
                    {u.name || "—"} {u.id === me?.id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                </div>
                <RoleBadge role={u.role} />
              </div>
              {["sales", "affiliate"].includes(u.role) && (
                <div className="mb-3 text-xs text-muted-foreground" data-testid={`user-codes-m-${i}`}>
                  Codes: {(u.referral_codes || []).length ? (u.referral_codes || []).join(", ") : "— none assigned"}
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{fmtDate(u.created_at)}</span>
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditForm({ name: u.name || "", email: u.email || "", role: u.role || "staff", referral_codes: (u.referral_codes || []).join(", ") }); setEditTarget(u); }} data-testid={`edit-user-m-${i}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setPwTarget(u); setNewPw(""); }} data-testid={`reset-pw-m-${i}`}>
                      <KeyRound className="h-3.5 w-3.5" />
                    </Button>
                    {u.id !== me?.id && (
                      <Button variant="outline" size="sm" onClick={() => setDelTarget(u)} data-testid={`delete-user-m-${i}`} className="text-destructive hover:bg-destructive/10">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-md border border-border bg-card md:block">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-border bg-[#EDF3F5] text-left">
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Role</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Added</th>
              {isAdmin && <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [...Array(3)].map((_, i) => (
                <tr key={i} className="border-b border-border">
                  <td colSpan={5} className="px-4 py-3"><div className="h-4 w-full animate-pulse rounded bg-[#E6EFF2]" /></td>
                </tr>
              ))
            ) : (
              users.map((u, i) => (
                <tr key={u.id} className="border-b border-border transition-colors hover:bg-[#E6EFF2]" data-testid={`user-row-${i}`}>
                  <td className="px-4 py-3 font-medium">
                    {u.name || "—"} {u.id === me?.id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    {isAdmin && u.id !== me?.id ? (
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        data-testid={`role-select-${i}`}
                        className="h-8 rounded-md border border-input bg-card px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#2b9bb7]"
                      >
                        <option value="staff">Staff</option>
                        <option value="admin">Admin</option>
                        <option value="sales">Sales</option>
                        <option value="affiliate">Affiliate</option>
                      </select>
                    ) : (
                      <RoleBadge role={u.role} />
                    )}
                    {["sales", "affiliate"].includes(u.role) && (
                      <div className="mt-1 text-xs text-muted-foreground" data-testid={`user-codes-${i}`}>
                        {(u.referral_codes || []).length ? (u.referral_codes || []).join(", ") : "— no codes assigned"}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(u.created_at)}</td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditForm({ name: u.name || "", email: u.email || "", role: u.role || "staff", referral_codes: (u.referral_codes || []).join(", ") }); setEditTarget(u); }} data-testid={`edit-user-${i}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setPwTarget(u); setNewPw(""); }} data-testid={`reset-pw-${i}`}>
                          <KeyRound className="h-3.5 w-3.5" />
                        </Button>
                        {u.id !== me?.id && (
                          <Button variant="outline" size="sm" onClick={() => setDelTarget(u)} data-testid={`delete-user-${i}`} className="text-destructive hover:bg-destructive/10">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add user dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>Add new user</DialogTitle>
            <DialogDescription>Create login credentials for a team member.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="Jane Doe" data-testid="new-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="jane@company.com" data-testid="new-user-email" />
            </div>
            <div className="space-y-2">
              <Label>Temporary password</Label>
              <Input value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} placeholder="min 6 characters" data-testid="new-user-password" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <RoleSelect value={addForm.role} onChange={(v) => setAddForm({ ...addForm, role: v })} testId="new-user-role" />
            </div>
            {["sales", "affiliate"].includes(addForm.role) && (
              <div className="space-y-2">
                <Label>Assigned referral code{addForm.role === "sales" ? "s" : ""}</Label>
                <Input
                  value={addForm.referral_codes}
                  onChange={(e) => setAddForm({ ...addForm, referral_codes: e.target.value })}
                  placeholder={addForm.role === "sales" ? "e.g. ABC123, XYZ789" : "e.g. ABC123"}
                  data-testid="new-user-codes"
                />
                <p className="text-xs text-muted-foreground">
                  {addForm.role === "sales"
                    ? "Comma-separated. This user will only see clients under these referral codes."
                    : "This affiliate will only see their own clients & commissions for this code."}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={createUser} disabled={busy} data-testid="submit-new-user">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>Update the user's name and email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <RoleSelect value={editForm.role} onChange={(v) => setEditForm({ ...editForm, role: v })} testId="edit-user-role" />
            </div>
            {["sales", "affiliate"].includes(editForm.role) && (
              <div className="space-y-2">
                <Label>Assigned referral code{editForm.role === "sales" ? "s" : ""}</Label>
                <Input
                  value={editForm.referral_codes}
                  onChange={(e) => setEditForm({ ...editForm, referral_codes: e.target.value })}
                  placeholder={editForm.role === "sales" ? "e.g. ABC123, XYZ789" : "e.g. ABC123"}
                  data-testid="edit-user-codes"
                />
                <p className="text-xs text-muted-foreground">
                  {editForm.role === "sales"
                    ? "Comma-separated. This user will only see clients under these referral codes."
                    : "This affiliate will only see their own clients & commissions for this code."}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="save-user-edit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && setPwTarget(null)}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Set a new password for {pwTarget?.email}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>New password</Label>
            <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="min 6 characters" data-testid="reset-pw-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwTarget(null)}>Cancel</Button>
            <Button onClick={resetPw} disabled={busy} data-testid="submit-reset-pw">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>Delete user</DialogTitle>
            <DialogDescription>
              Permanently delete <span className="font-medium text-foreground">{delTarget?.email}</span>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={removeUser} disabled={busy} data-testid="confirm-delete-user">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
